import React, { useState, useRef, useEffect } from 'react';
import dryEditorClass from './DryEditorClass';
import constants from '../../constants';
import FastTooltip from '../../components/FastTooltip';

const { EDITOR_COMMAND_NAME_MAP } = constants;

const DEFAULT_FONT_SIZE = 16;
const MIN_FONT_SIZE = 6;
const MAX_FONT_SIZE = 96;

const getFontSizeFromState = (editorState) => {
  if (!editorState) return 'inherit';
  try {
    const { $from, empty, from, to } = editorState.selection;
    const fontSizeMark = editorState.schema.marks.fontsize;
    if (!fontSizeMark) return 'inherit';
    if (empty) {
      const mark = $from.marks().find(m => m.type === fontSizeMark);
      return mark ? mark.attrs.fontSize : 'inherit';
    }
    let found = null;
    editorState.doc.nodesBetween(from, to, (node) => {
      if (node.isText && !found) {
        const mark = node.marks.find(m => m.type === fontSizeMark);
        if (mark) found = mark.attrs.fontSize;
      }
    });
    return found || 'inherit';
  } catch (_) {
    return 'inherit';
  }
};

const sizeToNum = (size) => {
  if (!size || size === 'inherit') return DEFAULT_FONT_SIZE;
  return parseInt(size, 10) || DEFAULT_FONT_SIZE;
};

const numToSize = (num) => {
  if (num === DEFAULT_FONT_SIZE) return 'inherit';
  return `${num}px`;
};

const FontSizeSelector = () => {
  const [currentSize, setCurrentSize] = useState('inherit');
  const [inputValue, setInputValue] = useState(String(DEFAULT_FONT_SIZE));
  const inputRef = useRef(null);

  useEffect(() => {
    const update = (state) => {
      const size = getFontSizeFromState(state);
      setCurrentSize(size);
      setInputValue(String(sizeToNum(size)));
    };
    dryEditorClass.registerTransactionHandler({ name: 'fontSizeSelector', method: update });
    return () => dryEditorClass.registeredTransactionHandlers.delete('fontSizeSelector');
  }, []);

  const applyNum = (num) => {
    const clamped = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, num));
    dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.font_size, options: { fontSize: numToSize(clamped) } }]);
    setInputValue(String(clamped));
  };

  const handleDecrement = (e) => {
    e.preventDefault();
    applyNum(sizeToNum(currentSize) - 1);
  };

  const handleIncrement = (e) => {
    e.preventDefault();
    applyNum(sizeToNum(currentSize) + 1);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputCommit = () => {
    const num = parseInt(inputValue, 10);
    if (!isNaN(num)) {
      applyNum(num);
    } else {
      setInputValue(String(sizeToNum(currentSize)));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
      handleInputCommit();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      applyNum(sizeToNum(currentSize) + 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      applyNum(sizeToNum(currentSize) - 1);
    }
  };

  return (
    <FastTooltip title="Font size">
      <div className="font-size-stepper">
        <button
          className="font-size-stepper__btn"
          onMouseDown={handleDecrement}
          type="button"
          aria-label="Decrease font size"
        >
          −
        </button>
        <input
          ref={inputRef}
          className="font-size-stepper__input"
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputCommit}
          onKeyDown={handleKeyDown}
          aria-label="Font size"
        />
        <button
          className="font-size-stepper__btn"
          onMouseDown={handleIncrement}
          type="button"
          aria-label="Increase font size"
        >
          +
        </button>
      </div>
    </FastTooltip>
  );
};

export default FontSizeSelector;
